import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Briefcase,
  ChevronDown,
  Code2,
  FlaskConical,
  Globe,
  Image,
  Layers,
  Loader2,
  MapPin,
  Smartphone,
  TrendingUp,
  Upload,
  Users,
  X,
  Zap,
} from "lucide-react";
import Reveal from "../components/ui/Reveal";
import SEO from "../components/seo/SEO";

import { API_BASE_URL } from "../config/api";

// ── Open Roles ────────────────────────────────────────────────────────────────
const ROLES = [
  {
    id: "backend-engineer",
    title: "Backend Engineer",
    icon: Code2,
    type: "Full-time",
    location: "Remote (Nigeria)",
    team: "Engineering",
    urgent: true,
    summary:
      "Own and scale the Laravel backend that powers CampusGenie for thousands of Nigerian students, from the AI-driven quiz engine and RAG chatbot to push notifications, queue jobs, and the core API the mobile app depends on.",
    responsibilities: [
      "Extend and maintain the Laravel REST API consumed by the CampusGenie mobile app",
      "Own database schema design, Eloquent ORM models, and query optimisation for scale",
      "Build and maintain queue jobs for background tasks: content generation, email, notifications",
      "Integrate and iterate on the Google Gemini AI pipeline powering quiz generation and the RAG chatbot",
      "Manage Expo push notification delivery via the custom broadcasting channel",
      "Write Feature tests with Pest PHP and maintain high test coverage across all endpoints",
      "Set up and improve CI/CD pipelines and cloud infrastructure",
      "Collaborate directly with the founder to ship features fast without sacrificing reliability",
    ],
    requirements: [
      "3+ years building production backend systems",
      "Strong PHP proficiency with hands-on Laravel experience (routing, middleware, Eloquent, queues)",
      "Solid MySQL / relational database experience: schema design, indexing, and query tuning",
      "Experience with REST API design, Sanctum / JWT auth, and webhook integrations",
      "Comfortable writing tests with Pest PHP or PHPUnit",
      "Comfortable with cloud deployment (Docker, Railway, Render, AWS, or GCP)",
      "A bias for shipping: you get things done without waiting for perfect",
    ],
    niceToHave: [
      "Experience integrating LLMs or Google Gemini API into production applications",
      "Familiarity with Expo push notifications or Firebase Cloud Messaging",
      "Knowledge of Laravel Horizon, Redis, and queue worker management",
      "Experience in EdTech or fintech in an African market",
    ],
  },
  {
    id: "mobile-developer",
    title: "Mobile Developer",
    icon: Smartphone,
    type: "Full-time",
    location: "Remote (Nigeria)",
    team: "Engineering",
    urgent: true,
    summary:
      "Build and maintain the CampusGenie mobile app used by Nigerian students every day, from JAMB prep to university life. You'll own the iOS and Android experience end to end.",
    responsibilities: [
      "Build and maintain features across iOS and Android using React Native",
      "Implement smooth animations, offline support, and fast load times",
      "Integrate with the backend API and third-party SDKs (payments, notifications, analytics)",
      "Own app store submissions, release cycles, and version management",
      "Work closely with the product designer to implement pixel-perfect UIs",
      "Debug performance issues and optimise for low-end Android devices common in Nigeria",
    ],
    requirements: [
      "3+ years of mobile development experience",
      "Strong React Native with shipped apps on both stores",
      "Deep understanding of mobile UX patterns, gestures, and navigation",
      "Experience with state management (Redux)",
      "Comfortable handling app store review processes and release management",
      "Understanding of mobile performance: memory, battery, and network constraints",
    ],
    niceToHave: [
      "Experience building for Nigerian or emerging-market users (low connectivity, device variety)",
      "Knowledge of Expo or bare React Native workflow",
      "Familiarity with in-app purchase integration for iOS and Android",
    ],
  },
  {
    id: "growth-community-manager",
    title: "Growth & Community Manager",
    icon: Users,
    type: "Full-time",
    location: "Lagos / Remote (Nigeria)",
    team: "Growth",
    urgent: false,
    summary:
      "Own student acquisition and community across Nigeria's universities and secondary schools. You'll build the campus ambassador program, manage our online communities, and turn CampusGenie into the app every student knows.",
    responsibilities: [
      "Design and run the CampusGenie campus ambassador programme across Nigerian universities",
      "Manage and grow our WhatsApp, Telegram, and social media communities",
      "Plan and execute on-campus activations, exam season campaigns, and giveaways",
      "Create content that resonates with Nigerian students (JAMB season, results day, matriculation)",
      "Track and report growth metrics: installs, referrals, DAU, community size",
      "Identify and partner with student influencers, course reps, and student unions",
    ],
    requirements: [
      "2+ years in community management, campus marketing, or growth roles",
      "Deep understanding of Nigerian student culture: you've lived it",
      "Proven experience growing online communities (WhatsApp, Telegram, Twitter/X, TikTok)",
      "Strong written communication and content creation skills",
      "Data-driven: comfortable tracking KPIs and iterating based on results",
      "Self-starter who can run campaigns independently without hand-holding",
    ],
    niceToHave: [
      "Existing network of student leaders, course reps, or student union contacts",
      "Experience with Nigerian JAMB/WAEC season marketing",
      "Video content creation or short-form video editing skills",
    ],
  },
  {
    id: "bizdev-partnerships",
    title: "Business Development & Partnerships Lead",
    icon: TrendingUp,
    type: "Full-time",
    location: "Lagos (Hybrid)",
    team: "Business",
    urgent: false,
    summary:
      "Own the B2B side of CampusGenie: signing universities, secondary schools, and EdTech partners. You'll be the person who walks into a VC's office or a university's ICT department and closes the deal.",
    responsibilities: [
      "Identify, pitch, and close partnerships with Nigerian universities and secondary schools",
      "Build and manage a pipeline of institutional clients from first contact to signed agreement",
      "Represent CampusGenie at EdTech conferences, expos, and government education forums",
      "Structure partnership and licensing agreements with support from legal",
      "Work with the product team to translate institutional needs into the product roadmap",
      "Build relationships with government education bodies (NUC, JAMB, WAEC, UBEC)",
    ],
    requirements: [
      "3+ years in B2B sales, business development, or institutional partnerships",
      "Experience selling to Nigerian universities, schools, or government bodies",
      "Strong negotiation, presentation, and relationship management skills",
      "Comfortable managing long sales cycles (3 to 12 months from lead to close)",
      "Existing network within Nigerian education or EdTech is a strong advantage",
      "Ambitious: you want equity and to grow with the company, not just a salary",
    ],
    niceToHave: [
      "Understanding of Nigerian university procurement processes",
      "Experience in SaaS, EdTech, or government tech sales",
      "Existing relationships with NUC, JAMB board, or major university ICT teams",
    ],
  },
  {
    id: "product-designer",
    title: "Product Designer",
    icon: Layers,
    type: "Full-time",
    location: "Remote (Nigeria)",
    team: "Design",
    urgent: false,
    summary:
      "Shape the experience of CampusGenie for hundreds of thousands of Nigerian students. You'll own end-to-end product design, from research and wireframes to final high-fidelity screens handed off to engineering.",
    responsibilities: [
      "Own the full product design process: research, flows, wireframes, prototypes, and final UI",
      "Design mobile-first screens for iOS and Android that feel native and intuitive",
      "Maintain and evolve the CampusGenie design system: components, tokens, and patterns",
      "Work directly with the founder and engineers to ship features quickly without losing quality",
      "Conduct user research and usability testing with Nigerian students to validate design decisions",
      "Create interaction specifications and detailed handoff documentation for developers",
    ],
    requirements: [
      "3+ years of product design experience with a strong mobile portfolio",
      "Expert-level Figma proficiency: components, auto-layout, variables, and prototyping",
      "Strong grasp of mobile UX principles: navigation patterns, gestures, accessibility",
      "Experience designing for real users, not just aesthetics: research-informed decisions",
      "Able to work fast in a startup environment without sacrificing craft",
      "Strong visual design eye: typography, spacing, colour, and hierarchy",
    ],
    niceToHave: [
      "Experience designing for Nigerian or emerging-market users",
      "Understanding of React Native component constraints",
      "Motion design skills: micro-interactions and transitions in Figma or Rive",
    ],
  },
  {
    id: "qa-tester",
    title: "QA Engineer (Mobile & Web)",
    icon: FlaskConical,
    type: "Full-time",
    location: "Remote (Nigeria)",
    team: "Engineering",
    urgent: false,
    summary:
      "Be the last line of defence before features reach Nigerian students. You'll own quality across the CampusGenie mobile app and web platform, finding bugs before users do and making sure every release ships with confidence.",
    responsibilities: [
      "Write and execute test cases for new features on iOS, Android, and the web platform",
      "Perform functional, regression, and exploratory testing on every release",
      "Test across a wide range of Android devices and OS versions common in Nigeria",
      "Identify, document, and track bugs with clear reproduction steps in Notion or Linear",
      "Work closely with engineers and the founder to prioritise and verify bug fixes",
      "Build and maintain a library of manual and automated test cases over time",
      "Validate API responses against backend documentation and flag inconsistencies",
    ],
    requirements: [
      "2+ years of QA or software testing experience: mobile testing is a must",
      "Hands-on experience testing React Native or Flutter apps on real devices",
      "Solid understanding of the software development lifecycle and release management",
      "Strong attention to detail: you catch the things everyone else misses",
      "Ability to write clear, reproducible bug reports with steps, screenshots, and screen recordings",
      "Comfortable testing under real-world Nigerian conditions: low bandwidth, device variety",
    ],
    niceToHave: [
      "Experience with test automation tools (Detox, Appium, Playwright, or Cypress)",
      "API testing experience with tools like Postman or Insomnia",
      "Knowledge of performance and load testing basics",
    ],
  },
  {
    id: "creative-designer",
    title: "Creative Designer",
    icon: Image,
    type: "Full-time",
    location: "Remote (Nigeria)",
    team: "Design",
    urgent: false,
    summary:
      "Create the visual identity of CampusGenie across every touchpoint: social media, campaigns, banners, flyers, and marketing materials. You'll make CampusGenie unmistakable and impossible to scroll past.",
    responsibilities: [
      "Design social media content for Instagram, Twitter/X, TikTok, LinkedIn, and WhatsApp",
      "Create campaign visuals for JAMB season, WAEC results, matriculation, and other key student moments",
      "Produce digital and print-ready flyers, banners, and event materials for campus activations",
      "Develop branded templates the growth team can use independently for ongoing content",
      "Collaborate with the Growth & Community Manager to align visuals with campaign messaging",
      "Maintain brand consistency across all creative outputs: colour, typography, tone, and style",
    ],
    requirements: [
      "2+ years of graphic/visual design experience with a strong creative portfolio",
      "Expert in Figma, Adobe Illustrator, or Photoshop, or all three",
      "Strong understanding of social media formats, dimensions, and platform-specific best practices",
      "Ability to produce high volumes of content quickly without losing quality",
      "Deep feel for what resonates with Nigerian youth culture: memes, trends, and visual language",
      "Brand-conscious: you keep things consistent, not just pretty",
    ],
    niceToHave: [
      "Experience with video editing or short-form video (CapCut, Premiere, After Effects)",
      "Motion graphics skills for animated social posts and Stories",
      "Photography or content direction experience for student-facing campaigns",
    ],
  },
];

const PERKS = [
  { icon: Globe, title: "Remote-first", description: "Work from anywhere in Nigeria. We care about output, not location." },
  { icon: Zap, title: "Move fast", description: "No bureaucracy. Your work ships directly to thousands of students." },
  { icon: TrendingUp, title: "Equity", description: "We offer meaningful equity. You build it, you own a piece of it." },
  { icon: Briefcase, title: "Real ownership", description: "Own your domain fully. No micromanagement, no hand-holding." },
];

const VALUES = [
  { label: "Student-first", description: "Every decision starts with: does this make life better for a Nigerian student?" },
  { label: "Ship, then improve", description: "We'd rather ship something good today than something perfect next quarter." },
  { label: "Radical transparency", description: "Numbers, strategy, and challenges are shared openly with the whole team." },
  { label: "Builder mindset", description: "We hire people who figure things out, not people who wait to be told." },
];

// ── Role Card ─────────────────────────────────────────────────────────────────
function RoleCard({ role, onApply }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = role.icon;

  return (
    <div
      style={{
        background: "var(--color-surface-elevated)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-2xl)",
        overflow: "hidden",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--color-primary)";
        e.currentTarget.style.boxShadow = "0 0 0 1px var(--color-primary)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--color-border)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Header row */}
      <div style={{ padding: "var(--space-6)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--space-4)", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
            <div style={{
              width: 44, height: 44,
              borderRadius: "var(--radius-xl)",
              background: "var(--color-primary-soft)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <Icon size={22} style={{ color: "var(--color-primary)" }} />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap" }}>
                <h3 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-semibold)", margin: 0 }}>
                  {role.title}
                </h3>
                {role.urgent && (
                  <span style={{
                    fontSize: "11px", fontWeight: 700, letterSpacing: "0.05em",
                    padding: "2px 8px", borderRadius: "var(--radius-full)",
                    background: "rgba(239,68,68,0.1)", color: "#ef4444",
                    textTransform: "uppercase",
                  }}>
                    Urgent
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "4px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: "4px" }}>
                  <MapPin size={13} /> {role.location}
                </span>
                <span style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" }}>· {role.type}</span>
                <span style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" }}>· {role.team}</span>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onApply(role)}
              style={{ fontSize: "var(--text-sm)", padding: "8px 18px" }}
            >
              Apply
            </button>
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              style={{
                display: "flex", alignItems: "center", gap: "4px",
                padding: "8px 12px",
                background: "transparent",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-lg)",
                color: "var(--color-text-secondary)",
                cursor: "pointer",
                fontSize: "var(--text-sm)",
                transition: "all 0.15s ease",
              }}
              aria-expanded={expanded}
            >
              {expanded ? "Less" : "Details"}
              <ChevronDown size={14} style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }} />
            </button>
          </div>
        </div>

        <p style={{ marginTop: "var(--space-4)", color: "var(--color-text-secondary)", fontSize: "var(--text-sm)", lineHeight: 1.65, margin: "var(--space-4) 0 0" }}>
          {role.summary}
        </p>
      </div>

      {/* Expandable JD */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="jd"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{ overflow: "hidden" }}
          >
            <div style={{
              padding: "0 var(--space-6) var(--space-6)",
              borderTop: "1px solid var(--color-border)",
              paddingTop: "var(--space-6)",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "var(--space-6)",
            }}>
              <JDSection title="What you'll do" items={role.responsibilities} accent />
              <JDSection title="What we're looking for" items={role.requirements} />
              {role.niceToHave?.length > 0 && (
                <JDSection title="Nice to have" items={role.niceToHave} muted />
              )}
            </div>
            <div style={{ padding: "0 var(--space-6) var(--space-6)" }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => onApply(role)}
                style={{ width: "100%", justifyContent: "center" }}
              >
                Apply for {role.title}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function JDSection({ title, items, accent, muted }) {
  return (
    <div>
      <h4 style={{
        fontSize: "var(--text-sm)",
        fontWeight: "var(--font-semibold)",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        color: accent ? "var(--color-primary)" : muted ? "var(--color-text-tertiary)" : "var(--color-text-primary)",
        marginBottom: "var(--space-3)",
      }}>
        {title}
      </h4>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        {items.map((item, i) => (
          <li key={i} style={{ display: "flex", gap: "var(--space-2)", alignItems: "flex-start", fontSize: "var(--text-sm)", color: muted ? "var(--color-text-tertiary)" : "var(--color-text-secondary)", lineHeight: 1.6 }}>
            <span style={{ color: accent ? "var(--color-primary)" : "var(--color-text-tertiary)", marginTop: "5px", flexShrink: 0, fontSize: "8px" }}>●</span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Application Modal ─────────────────────────────────────────────────────────
function ApplicationModal({ role, onClose }) {
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    linkedin: "", portfolio: "", whyCampusGenie: "",
  });
  const [cvFile, setCvFile] = useState(null);   // actual File object
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) setCvFile(file);
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.lastName.trim()) e.lastName = "Required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required";
    if (!form.phone.trim()) e.phone = "Required";
    if (!form.whyCampusGenie.trim() || form.whyCampusGenie.trim().length < 50)
      e.whyCampusGenie = "Tell us a bit more (at least 50 characters)";
    if (!cvFile) e.cv = "Please attach your CV";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setErrorMsg("");

    if (!API_BASE_URL) {
      setErrorMsg("Application service is unavailable right now. Please try again later.");
      setStatus("error");
      return;
    }

    setStatus("loading");

    try {
      // multipart/form-data  do NOT set Content-Type header manually;
      // the browser sets it automatically with the correct boundary.
      const formData = new FormData();
      formData.append("first_name",      form.firstName.trim());
      formData.append("last_name",       form.lastName.trim());
      formData.append("email",           form.email.trim());
      formData.append("phone_number",    form.phone.trim());
      formData.append("role",            role.id);
      formData.append("role_title",      role.title);
      formData.append("why_campusgenie", form.whyCampusGenie.trim());
      formData.append("cv_file",         cvFile, cvFile.name);
      if (form.linkedin.trim())  formData.append("linkedin_url",  form.linkedin.trim());
      if (form.portfolio.trim()) formData.append("portfolio_url", form.portfolio.trim());

      const response = await fetch(`${API_BASE_URL}/careers/apply`, {
        method: "POST",
        body: formData,
        // No Content-Type header  browser sets multipart/form-data + boundary
      });

      const json = await response.json();

      if (!response.ok || !json.success) {
        throw new Error(json.message || "Submission failed");
      }

      setStatus("success");
    } catch (err) {
      setErrorMsg(err.message || "We couldn't submit your application. Please try again.");
      setStatus("error");
    }
  };

  // Close on Escape
  React.useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "var(--space-4)",
        overflowY: "auto",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        transition={{ duration: 0.2 }}
        style={{
          background: "var(--color-bg-primary)",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius-3xl)",
          width: "100%",
          maxWidth: 560,
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
        }}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          style={{
            position: "sticky", top: "var(--space-5)",
            float: "right",
            marginRight: "var(--space-5)",
            width: 32, height: 32,
            borderRadius: "50%",
            background: "var(--color-surface-elevated)",
            border: "1px solid var(--color-border)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", zIndex: 10,
            color: "var(--color-text-secondary)",
          }}
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div style={{ padding: "var(--space-8)" }}>
          {status === "success" ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ textAlign: "center", paddingTop: "var(--space-4)" }}
            >
              <div style={{
                width: 64, height: 64, borderRadius: "50%",
                background: "var(--color-primary-soft)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto var(--space-5)",
              }}>
                <Zap size={30} style={{ color: "var(--color-primary)" }} />
              </div>
              <h3 className="heading-3" style={{ marginBottom: "var(--space-3)" }}>Application received!</h3>
              <p style={{ color: "var(--color-text-secondary)", marginBottom: "var(--space-6)" }}>
                Thanks for applying for <strong>{role.title}</strong>. We review every application personally and will be in touch within 5–7 business days.
              </p>
              <button type="button" className="btn btn-primary" onClick={onClose} style={{ width: "100%", justifyContent: "center" }}>
                Done
              </button>
            </motion.div>
          ) : (
            <>
              {/* Modal header */}
              <div style={{ marginBottom: "var(--space-6)" }}>
                <span style={{
                  fontSize: "var(--text-xs)", fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.08em", color: "var(--color-primary)",
                }}>
                  {role.team} · {role.type}
                </span>
                <h2 className="heading-3" style={{ marginTop: "var(--space-1)" }}>Apply  {role.title}</h2>
                <p style={{ color: "var(--color-text-secondary)", fontSize: "var(--text-sm)", marginTop: "4px" }}>
                  All fields marked * are required.
                </p>
              </div>

              {status === "error" && (
                <div style={{
                  padding: "12px 16px",
                  background: "rgba(239,68,68,0.08)",
                  border: "1px solid rgba(239,68,68,0.25)",
                  borderRadius: "var(--radius-lg)",
                  color: "#ef4444",
                  fontSize: "var(--text-sm)",
                  marginBottom: "var(--space-5)",
                }}>
                  {errorMsg || "We couldn't submit your application. Please try again."}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                {/* Name row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
                  <AppField id="firstName" label="First Name" error={errors.firstName}>
                    <input id="firstName" type="text" className={`form-input ${errors.firstName ? "input-error" : ""}`}
                      placeholder="Ada" value={form.firstName} onChange={set("firstName")} autoComplete="given-name" />
                  </AppField>
                  <AppField id="lastName" label="Last Name" error={errors.lastName}>
                    <input id="lastName" type="text" className={`form-input ${errors.lastName ? "input-error" : ""}`}
                      placeholder="Okafor" value={form.lastName} onChange={set("lastName")} autoComplete="family-name" />
                  </AppField>
                </div>

                <AppField id="email" label="Email Address" error={errors.email}>
                  <input id="email" type="email" className={`form-input ${errors.email ? "input-error" : ""}`}
                    placeholder="you@email.com" value={form.email} onChange={set("email")} autoComplete="email" />
                </AppField>

                <AppField id="phone" label="Phone Number" error={errors.phone}>
                  <input id="phone" type="tel" className={`form-input ${errors.phone ? "input-error" : ""}`}
                    placeholder="08012345678" value={form.phone} onChange={set("phone")} autoComplete="tel" />
                </AppField>

                <AppField id="linkedin" label="LinkedIn Profile" error={errors.linkedin} optional>
                  <input id="linkedin" type="url" className="form-input"
                    placeholder="https://linkedin.com/in/yourprofile" value={form.linkedin} onChange={set("linkedin")} />
                </AppField>

                <AppField id="portfolio" label="Portfolio / GitHub / Behance" error={errors.portfolio} optional>
                  <input id="portfolio" type="url" className="form-input"
                    placeholder="https://github.com/yourusername" value={form.portfolio} onChange={set("portfolio")} />
                </AppField>

                <AppField id="whyCampusGenie" label="Why CampusGenie?" error={errors.whyCampusGenie}>
                  <textarea
                    id="whyCampusGenie"
                    className={`form-textarea ${errors.whyCampusGenie ? "input-error" : ""}`}
                    placeholder="Tell us why you want to join CampusGenie and what you'll bring to the team. Be specific. Generic answers don't stand out."
                    value={form.whyCampusGenie}
                    onChange={set("whyCampusGenie")}
                    rows={5}
                    style={{ resize: "vertical", minHeight: 120 }}
                  />
                  <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" }}>
                    {form.whyCampusGenie.length} chars {form.whyCampusGenie.length < 50 ? `· ${50 - form.whyCampusGenie.length} more to go` : "· ✓"}
                  </span>
                </AppField>

                {/* CV Upload */}
                <div className="form-group">
                  <label className="form-label">
                    CV / Resume <span style={{ color: "var(--color-primary)" }}>*</span>
                  </label>
                  <label
                    htmlFor="cv-upload"
                    style={{
                      display: "flex", alignItems: "center", gap: "var(--space-3)",
                      padding: "var(--space-4)",
                      border: `2px dashed ${errors.cv ? "#ef4444" : "var(--color-border)"}`,
                      borderRadius: "var(--radius-lg)",
                      cursor: "pointer",
                      background: cvFile ? "var(--color-primary-soft)" : "var(--color-bg-secondary)",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <Upload size={20} style={{ color: cvFile ? "var(--color-primary)" : "var(--color-text-tertiary)", flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-semibold)", color: cvFile ? "var(--color-primary)" : "var(--color-text-primary)" }}>
                        {cvFile ? cvFile.name : "Click to upload your CV"}
                      </div>
                      <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginTop: "2px" }}>
                        PDF, DOC, DOCX · max 10MB
                      </div>
                    </div>
                    <input
                      id="cv-upload"
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleFile}
                      style={{ display: "none" }}
                    />
                  </label>
                  {errors.cv && (
                    <p style={{ color: "#ef4444", fontSize: "var(--text-sm)", marginTop: "6px" }}>{errors.cv}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={status === "loading"}
                  style={{ width: "100%", justifyContent: "center", marginTop: "var(--space-2)" }}
                >
                  {status === "loading" ? (
                    <><Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Submitting…</>
                  ) : (
                    "Submit Application →"
                  )}
                </button>

                <p style={{ textAlign: "center", fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginTop: "var(--space-3)" }}>
                  We review every application personally. Expect a response within 5–7 business days.
                </p>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// Field wrapper for modal  defined outside to prevent remount
function AppField({ id, label, error, optional, children }) {
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={id}>
        {label}{" "}
        {optional
          ? <span style={{ color: "var(--color-text-tertiary)", fontWeight: 400 }}>(optional)</span>
          : <span style={{ color: "var(--color-primary)" }}>*</span>
        }
      </label>
      {children}
      {error && <p style={{ color: "#ef4444", fontSize: "var(--text-sm)", marginTop: "6px" }}>{error}</p>}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function CareersPage() {
  const [applyRole, setApplyRole] = useState(null);

  return (
    <>
      <SEO
        title="Careers · Join the CampusGenie Team"
        description="Help us build Nigeria's student OS. We're hiring backend engineers, mobile developers, growth managers, and business development leads. Remote-first, equity included."
        url="/careers"
      />

      {/* Hero */}
      <section className="hero" style={{ minHeight: "auto", paddingBottom: "var(--space-4)" }}>
        <div className="container">
          <Reveal>
            <div style={{ maxWidth: 680 }}>
              <div className="hero-badge" style={{ display: "inline-flex", marginBottom: "var(--space-5)" }}>
                <span className="hero-badge-dot" />
                We're hiring
              </div>
              <h1 className="heading-hero hero-title" style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)" }}>
                Help us build <span className="text-gradient">Nigeria's student OS.</span>
              </h1>
              <p className="body-large" style={{ color: "var(--color-text-secondary)", marginTop: "var(--space-4)", maxWidth: 540 }}>
                We're a small team moving fast. Every person here has direct impact on hundreds of thousands of Nigerian students. If you want to build something that matters, you're in the right place.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Perks */}
      <section className="section" style={{ paddingTop: "var(--space-10)", paddingBottom: "var(--space-10)" }}>
        <div className="container">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "var(--space-5)" }}>
            {PERKS.map((perk, i) => {
              const Icon = perk.icon;
              return (
                <Reveal key={perk.title} delay={i * 0.07}>
                  <div style={{
                    padding: "var(--space-5)",
                    background: "var(--color-surface-elevated)",
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-2xl)",
                  }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: "var(--radius-lg)",
                      background: "var(--color-primary-soft)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      marginBottom: "var(--space-3)",
                    }}>
                      <Icon size={20} style={{ color: "var(--color-primary)" }} />
                    </div>
                    <h4 style={{ fontWeight: "var(--font-semibold)", marginBottom: "var(--space-1)" }}>{perk.title}</h4>
                    <p style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0 }}>{perk.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section section-alt" style={{ paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center" style={{ marginBottom: "var(--space-10)" }}>
              <span className="eyebrow">How we work</span>
              <h2 className="heading-2">Built on these values.</h2>
            </div>
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "var(--space-5)" }}>
            {VALUES.map((v, i) => (
              <Reveal key={v.label} delay={i * 0.07}>
                <div style={{ padding: "var(--space-5) 0" }}>
                  <div style={{
                    display: "inline-block",
                    padding: "3px 10px",
                    borderRadius: "var(--radius-full)",
                    background: "var(--color-primary-soft)",
                    color: "var(--color-primary)",
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-semibold)",
                    marginBottom: "var(--space-3)",
                  }}>
                    {v.label}
                  </div>
                  <p style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0 }}>{v.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Open Roles */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="section-heading" style={{ marginBottom: "var(--space-10)" }}>
              <span className="eyebrow">Open positions</span>
              <h2 className="heading-2">{ROLES.length} open roles</h2>
              <p className="body-large" style={{ color: "var(--color-text-secondary)" }}>
                All roles are full-time. Equity is part of every offer.
              </p>
            </div>
          </Reveal>

          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            {ROLES.map((role, i) => (
              <Reveal key={role.id} delay={i * 0.07}>
                <RoleCard role={role} onApply={setApplyRole} />
              </Reveal>
            ))}
          </div>

          {/* General application */}
          <Reveal delay={0.2}>
            <div style={{
              marginTop: "var(--space-12)",
              padding: "var(--space-8)",
              background: "var(--color-surface-glass)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-3xl)",
              textAlign: "center",
            }}>
              <h3 className="heading-3" style={{ marginBottom: "var(--space-3)" }}>Don't see your role?</h3>
              <p style={{ color: "var(--color-text-secondary)", marginBottom: "var(--space-5)", maxWidth: 420, margin: "0 auto var(--space-5)" }}>
                If you're exceptional at what you do and believe in what we're building, send us a note anyway.
              </p>
              <a
                href="mailto:careers@campusgenie.app?subject=General Application · CampusGenie"
                className="btn btn-secondary"
              >
                careers@campusgenie.app
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Application Modal */}
      <AnimatePresence>
        {applyRole && (
          <ApplicationModal
            role={applyRole}
            onClose={() => setApplyRole(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
